import './DocumentList.scss'
import '../../../components/common/AppTable.scss'
import {
    Button,
    CircularProgress,
    Grid,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery
} from "@mui/material";
import React, {useEffect, useState} from "react";
import SearchIcon from '@mui/icons-material/Search';
import {useKeycloak} from "@react-keycloak/web";
import {ePismoClientInstance} from "../Service/EPismoClient";
import {log} from "node:util";

type DocumentSignatureDto = {
    type: string,
    number: string,
    title: string,
    documentDate: string,
    receiveDate: string,
    from : {
        name: string,
        address: string,
        id: number
    },
    to : {
        name: string,
        address: string,
        id: number
    },
    archived: boolean,
    inbox: boolean,
    emailFilled: boolean,
    sending: boolean,
    sent: boolean,
    sendingWithEmail: boolean,
}

type GetDocumentDto = {
    count:number,
    documents: DocumentSignatureDto[]
}
export default function DocumentList() {

    const keycloak = useKeycloak();
    const isMobile = useMediaQuery('(max-width:768px)');

    const [query, setQuery] = useState("")
    const [offset, setOffset] = useState(0)
    const [data, setData] = useState<GetDocumentDto>({count: 0, documents: []})
    const [loading, setLoading] = useState(false)
    const [emailInputs, setEmailInputs] = useState<Record<string, string>>({})
    const [documentStates, setDocumentStates] = useState<Record<string, {sending: boolean, sent: boolean, sendingWithEmail: boolean}>>({})

    function load() {
        setLoading(true)
        let params = new URLSearchParams();
        if (query)
            params.set("query", query)
        if (offset)
            params.set("offset", offset.toString())
        ePismoClientInstance.get("/v1/gui/documents?" + params.toString(), keycloak).then(r => {
            setData(r.data)
            setLoading(false)
        })
    }

    const downloadFile = (number: string) => {
        ePismoClientInstance.getFile('/v1/gui/documents/' + number + "/pdf", keycloak)
            .then(response => {
                console.log(response);
                const href = URL.createObjectURL(response.data);

                // create "a" HTML element with href to file & click
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', number + '.pdf'); //or any other extension
                document.body.appendChild(link);
                link.click();

                // clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
    }

    const send = (doc: DocumentSignatureDto) => {
        const docState = documentStates[doc.number] || {sending: doc.sending, sent: doc.sent, sendingWithEmail: doc.sendingWithEmail};
        
        if(docState.sendingWithEmail && emailInputs[doc.number]) {
            setDocumentStates(prev => ({
                ...prev,
                [doc.number]: {...docState, sending: true}
            }));
            
            const url = '/v1/gui/documents/' + doc.number + '/email?email=' + emailInputs[doc.number];
            ePismoClientInstance.post(url, keycloak, null).then(resp => {
                let responseData = resp.data;
                if(responseData.status === 'OK') {
                    setDocumentStates(prev => ({
                        ...prev,
                        [doc.number]: {sending: false, sent: true, sendingWithEmail: false}
                    }));
                } else {
                    setDocumentStates(prev => ({
                        ...prev,
                        [doc.number]: {sending: false, sent: false, sendingWithEmail: true}
                    }));
                }
                setEmailInputs(prev => {
                    const newInputs = {...prev};
                    delete newInputs[doc.number];
                    return newInputs;
                });
            }).catch(e => {
                setDocumentStates(prev => ({
                    ...prev,
                    [doc.number]: {sending: false, sent: false, sendingWithEmail: true}
                }));
            });
            return;
        }
        
        // Start sending
        setDocumentStates(prev => ({
            ...prev,
            [doc.number]: {...docState, sending: true}
        }));
        
        // For inbox documents, send without email. For non-inbox, try without email first
        const url = '/v1/gui/documents/' + doc.number + '/email';
        
        ePismoClientInstance.post(url, keycloak, null).then(resp => {
            let responseData = resp.data;
            if(responseData.status === 'OK') {
                setDocumentStates(prev => ({
                    ...prev,
                    [doc.number]: {sending: false, sent: true, sendingWithEmail: false}
                }));
            } else if(responseData.status === 'NO_EMAIL') {
                // Show email input field for non-inbox documents
                setDocumentStates(prev => ({
                    ...prev,
                    [doc.number]: {sending: false, sent: false, sendingWithEmail: true}
                }));
            } else {
                // ERROR status
                setDocumentStates(prev => ({
                    ...prev,
                    [doc.number]: {sending: false, sent: false, sendingWithEmail: false}
                }));
            }
        }).catch(e => {
            setDocumentStates(prev => ({
                ...prev,
                [doc.number]: {sending: false, sent: false, sendingWithEmail: false}
            }));
        })
    }

    const getDocumentState = (doc: DocumentSignatureDto) => {
        return documentStates[doc.number] || {sending: doc.sending, sent: doc.sent, sendingWithEmail: doc.sendingWithEmail};
    }

    useEffect(() => {
        load();
    }, [offset]);
    return (
        <div className="DocumentList">
            <Grid container spacing={3}>
                <Grid xs={12}>
                    <Typography align="left" variant="h1">Lista pism</Typography>
                </Grid>
                <Grid xs={12}>
                    <div className="searchBar" style={{textAlign:"left"}}>
                        <TextField onChange={e => setQuery(e.target.value)} label="Szukaj" size="small" sx={{mr: 2}} disabled={loading}/>
                        <Button variant="contained" onClick={load}>
                            {!loading && <><span className="searchBtnText" style={{marginRight:5}}>Szukaj</span> <SearchIcon /></>}
                            {loading && <>Trwa ładowanie...</>}
                        </Button>
                    </div>
                </Grid>
                <Grid xs={12}>
                    <Table className="appTable">
                        <TableHead>
                            <TableCell width="30%">Rodzaj dokumentu / Nr sygnatury</TableCell>
                            <TableCell width="16%">Data podpisania</TableCell>
                            <TableCell width="16%">Data odbioru</TableCell>
                            <TableCell width="25%">Odbiorca</TableCell>
                            <TableCell>Akcje</TableCell>
                        </TableHead>
                        <TableBody>
                            {data.documents.map( r =>
                                <TableRow>
                                    <TableCell data-label="Rodzaj dokumentu / Nr sygnatury">
                                        {!r.archived && <span className="link" onClick={e => downloadFile(r.number)}>{r.type}</span>}
                                        {!r.archived && <span>{r.title}</span>}
                                        {r.archived && <span>Dokument zarchiwizowany</span>}
                                        {r.number}</TableCell>
                                    <TableCell data-label="Data podpisania">{r.documentDate}</TableCell>
                                    <TableCell data-label="Data odbioru">{r.receiveDate}</TableCell>
                                    <TableCell data-label="Odbiorca"><span>{r.to.name}</span> <span>{r.to.address}</span></TableCell>
                                    <TableCell data-label="Akcje">
                                        {(() => {
                                            const docState = getDocumentState(r);
                                            const showEmailInput = docState.sendingWithEmail;
                                            
                                            // Only show button if not sent
                                            if (!docState.sent) {
                                                return (
                                                    <>
                                                        {showEmailInput && (
                                                            <TextField 
                                                                size="small" 
                                                                fullWidth 
                                                                label="Aby wysłać, wprowadź adres e-mail" 
                                                                variant="outlined" 
                                                                onChange={e => setEmailInputs(prev => ({...prev, [r.number]: e.target.value}))} 
                                                                onBlur={e => setEmailInputs(prev => ({...prev, [r.number]: e.target.value}))} 
                                                                value={emailInputs[r.number] || ''} 
                                                                sx={{mb: 1, minWidth: 200}}
                                                            />
                                                        )}
                                                        <Button 
                                                            variant="contained" 
                                                            size="small" 
                                                            onClick={() => send(r)}
                                                            disabled={docState.sending}
                                                            sx={{mr: 1}}
                                                        >
                                                            {!docState.sending && <>Wyślij</>}
                                                            {docState.sending && <><span>Wysyłanie...</span> <CircularProgress size={14} sx={{ml: 1}}/></>}
                                                        </Button>
                                                    </>
                                                );
                                            } else {
                                                return <span>Wysłano</span>;
                                            }
                                        })()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <Pagination count={Math.floor(data.count/5) + 1} siblingCount={isMobile ? 0 : 1} boundaryCount={1} onChange={(e, n) => setOffset((n-1)*5)} />
                </Grid>
            </Grid>
        </div>
    )
}